import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { actualizarMensaje } from "../../redux/actions/actGlobal";

import LayoutModulo from "./components/LayoutModulo";
import ProcesosGestion from "./gestion/ProcesosGestion";
import AdministrarGestion from "./gestion/AdministrarGestion";
import CalendarioGestion from "./gestion/CalendarioGestion";
import { Cargando, EstadoVacio } from "./components/Estados";

import { useModulo } from "./hooks/useModulo";
import { useConsulta } from "./hooks/useConsulta";
import { obtenerPerfilGestion } from "./services/bibliotecaApi";
import {
  PERFIL_AUXILIAR_BIBLIOTECA,
  RUTA_GESTION_BIBLIOTECA,
  VISTA_GESTION,
} from "./constants";
import { MENU_GESTION } from "./catalogo";

/**
 * Pantalla de quienes gestionan, con las pestañas de Gestion Practicas:
 * Procesos (las solicitudes que cada uno puede ver), Calendario (las mismas,
 * por fecha) y Administrar (permisos, horarios y periodo), que solo aparece
 * para el administrador general y el de biblioteca.
 *
 * Nada consulta hasta que hay sesion: si la pagina se abre mientras se inicia
 * sesion, las consultas sin token responden 403 y la pantalla se quedaba en
 * "Cargando..." (2026-10-14). Por eso todo depende del correo del usuario: al
 * llegar, o al cambiar de cuenta, se vuelve a consultar.
 */
const GestionBiblioteca = () => {
  const dispatch = useDispatch();

  useModulo(RUTA_GESTION_BIBLIOTECA, "Gestión Biblioteca");

  const correo = useSelector(({ redGlobal }) => redGlobal.usuario?.correo);
  const { datos: perfil, error } = useConsulta(
    () => (correo ? obtenerPerfilGestion() : Promise.resolve(null)),
    [correo],
    null,
  );
  const opciones = MENU_GESTION.filter(
    ({ codigo }) => codigo !== VISTA_GESTION.ADMINISTRAR || perfil?.configura,
  );

  const [vista, setVista] = useState(VISTA_GESTION.PROCESOS);
  // Pulsar una pestaña, aunque sea la actual, la devuelve a su inicio.
  const [reinicio, setReinicio] = useState(0);

  const onMensaje = useCallback(
    (titulo, tipo = "info") =>
      dispatch(actualizarMensaje({ titulo, tipo, mostrar: true, tiempo: 6000 })),
    [dispatch],
  );

  const contenido = () => {
    if (error) {
      return <EstadoVacio mensaje={error.message ?? "No fue posible cargar Gestión Biblioteca"} />;
    }
    // Se espera el perfil en todas las pestañas: sin el aun no hay sesion, y
    // el auxiliar entra al calendario con "Mi agenda".
    if (!perfil) return <Cargando />;
    if (vista === VISTA_GESTION.CALENDARIO) {
      return (
        <CalendarioGestion
          key={`${correo}-${reinicio}`}
          esAuxiliar={perfil?.perfil === PERFIL_AUXILIAR_BIBLIOTECA}
          onMensaje={onMensaje}
        />
      );
    }
    if (vista === VISTA_GESTION.ADMINISTRAR && perfil?.configura) {
      return <AdministrarGestion key={correo} onMensaje={onMensaje} />;
    }
    return <ProcesosGestion key={`${correo}-${reinicio}`} onMensaje={onMensaje} />;
  };

  return (
    <LayoutModulo
      opciones={opciones}
      seleccion={vista}
      onSeleccionar={(codigo) => {
        setVista(codigo);
        setReinicio((actual) => actual + 1);
      }}
    >
      {contenido()}
    </LayoutModulo>
  );
};

export default GestionBiblioteca;
